import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { CircleNotch, Buildings, ArrowLeft, Info } from "@phosphor-icons/react";
import axios from "axios";

const API = window.location.origin;

export default function Register() {
    const navigate = useNavigate();
    const [step, setStep] = useState(1); // 1=code, 2=details
    const [code, setCode] = useState("");
    const [preview, setPreview] = useState(null); // { scope, role, tenant_name, expires_at }
    const [checkingCode, setCheckingCode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [form, setForm] = useState({
        email: "",
        password: "",
        password2: "",
        imie_nazwisko: "",
        // Tenant (admin scope)
        company_name: "",
        nip: "",
        // Employee personal data
        pesel: "",
        data_urodzenia: "",
        numer_telefonu: "",
        numer_telefonu_zebra: "",
        numer_paszportu: "",
        numer_prawa_jazdy: "",
        numer_blankietu_prawa_jazdy: "",
        adres: "",
    });

    const isEmployeeInvite = preview?.role === "employee";
    const isNewTenant = preview?.scope === "new_tenant";

    const nextStep = async () => {
        setError(null);
        const clean = code.trim().toUpperCase();
        if (!clean || clean.length < 6) {
            setError("Podaj kod zaproszenia");
            return;
        }
        setCheckingCode(true);
        try {
            const { data } = await axios.get(`${API}/api/tenant/invite/preview`, { params: { code: clean } });
            setPreview(data);
            setStep(2);
        } catch (err) {
            setError(err?.response?.data?.detail || "Nieprawidłowy kod");
        } finally {
            setCheckingCode(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        if (form.password !== form.password2) {
            setError("Hasła nie są identyczne");
            return;
        }
        if (form.password.length < 8) {
            setError("Hasło musi mieć min. 8 znaków");
            return;
        }
        // Front-end guard for employee required fields (mirrors backend check)
        if (isEmployeeInvite) {
            const req = { pesel: "PESEL", data_urodzenia: "Data urodzenia", numer_telefonu: "Numer telefonu (komórka)", numer_paszportu: "Numer paszportu", numer_prawa_jazdy: "Numer prawa jazdy", adres: "Adres" };
            const miss = Object.entries(req).filter(([k]) => !form[k]?.trim()).map(([, label]) => label);
            if (miss.length) {
                setError(`Uzupełnij: ${miss.join(", ")}`);
                return;
            }
        }
        setLoading(true);
        try {
            const payload = {
                code: code.trim().toUpperCase(),
                email: form.email.trim().toLowerCase(),
                password: form.password,
                imie_nazwisko: form.imie_nazwisko.trim(),
                // Personal data (backend accepts all; ignores empty for admin invite)
                pesel: form.pesel.trim() || null,
                data_urodzenia: form.data_urodzenia.trim() || null,
                numer_telefonu: form.numer_telefonu.trim() || null,
                numer_telefonu_zebra: form.numer_telefonu_zebra.trim() || null,
                numer_paszportu: form.numer_paszportu.trim() || null,
                numer_prawa_jazdy: form.numer_prawa_jazdy.trim() || null,
                numer_blankietu_prawa_jazdy: form.numer_blankietu_prawa_jazdy.trim() || null,
                adres: form.adres.trim() || null,
            };
            if (isNewTenant) {
                payload.company_name = form.company_name.trim();
                payload.nip = form.nip.trim();
            }
            const { data } = await axios.post(`${API}/api/tenant/register`, payload);
            localStorage.setItem("access_token", data.access_token);
            localStorage.setItem("refresh_token", data.refresh_token);
            if (data.user?.skrot_status === "tymczasowy" && data.user?.name) {
                alert(
                    `Rejestracja zakończona!\n\nTwój tymczasowy skrót (login): ${data.user.name}\n\n` +
                    `Możesz logować się tym skrótem lub emailem. Administrator wkrótce nada Ci docelowy skrót.`
                );
            }
            navigate(data.user?.role === "employee" ? "/dashboard" : "/admin");
            window.location.reload();
        } catch (err) {
            setError(err?.response?.data?.detail || "Nie udało się zarejestrować");
        } finally {
            setLoading(false);
        }
    };

    const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:border-blue-500 focus:outline-none";

    return (
        <div className="min-h-screen w-full flex bg-slate-50">
            <div className="w-full sm:w-[640px] px-6 sm:px-14 py-8 sm:py-16 flex flex-col justify-center bg-white shadow-xl overflow-y-auto">
                <div className="mb-6">
                    <h1 className="font-heading text-4xl font-extrabold tracking-tight mb-2 text-slate-900" data-testid="register-title">
                        Fleetly
                    </h1>
                    <p className="text-slate-500">
                        {step === 1
                            ? "Załóż konto z kodem zaproszenia"
                            : isEmployeeInvite
                            ? `Rejestracja pracownika${preview?.tenant_name ? ` — ${preview.tenant_name}` : ""}`
                            : isNewTenant
                            ? "Rejestracja nowej firmy"
                            : "Rejestracja administratora"}
                    </p>
                </div>

                {step === 1 && (
                    <div className="space-y-4" data-testid="register-step-code">
                        <div>
                            <label className="text-xs font-medium text-slate-600 block mb-1">Kod zaproszenia</label>
                            <input
                                type="text"
                                value={code}
                                onChange={(e) => setCode(e.target.value.toUpperCase())}
                                placeholder="XXXX-XXXX"
                                className="w-full px-4 py-3 border border-slate-300 rounded-md text-lg font-mono tracking-widest text-center"
                                data-testid="register-code-input"
                                autoFocus
                            />
                        </div>
                        {error && <div className="text-sm text-red-600" data-testid="register-error">{error}</div>}
                        <button
                            onClick={nextStep}
                            disabled={checkingCode}
                            className="w-full px-4 py-3 bg-blue-700 hover:bg-blue-800 text-white rounded-md text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                            data-testid="register-next-btn"
                        >
                            {checkingCode ? <CircleNotch className="w-4 h-4 animate-spin" /> : null}
                            Dalej
                        </button>
                        <Link to="/login" className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mt-4" data-testid="register-back-to-login">
                            <ArrowLeft className="w-3.5 h-3.5" /> Mam już konto
                        </Link>
                    </div>
                )}

                {step === 2 && (
                    <form onSubmit={submit} className="space-y-3" data-testid="register-step-details">
                        {isEmployeeInvite && (
                            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-900 flex gap-2">
                                <Info className="w-4 h-4 shrink-0 mt-0.5" weight="fill" />
                                <div>
                                    Wypełnij wszystkie swoje dane. Otrzymasz <b>tymczasowy skrót</b> (login) — administrator wkrótce nada Ci docelowy skrót.
                                </div>
                            </div>
                        )}

                        <div>
                            <label className="text-xs font-medium text-slate-600 block mb-1">Imię i nazwisko *</label>
                            <input
                                type="text"
                                value={form.imie_nazwisko}
                                onChange={(e) => setForm({ ...form, imie_nazwisko: e.target.value })}
                                required
                                className={inputCls}
                                data-testid="register-name"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-slate-600 block mb-1">Email *</label>
                            <input
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                required
                                className={inputCls}
                                data-testid="register-email"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="text-xs font-medium text-slate-600 block mb-1">Hasło *</label>
                                <input
                                    type="password"
                                    value={form.password}
                                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                                    required minLength={8}
                                    className={inputCls}
                                    data-testid="register-password"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-slate-600 block mb-1">Powtórz hasło *</label>
                                <input
                                    type="password"
                                    value={form.password2}
                                    onChange={(e) => setForm({ ...form, password2: e.target.value })}
                                    required minLength={8}
                                    className={inputCls}
                                    data-testid="register-password2"
                                />
                            </div>
                        </div>

                        {isEmployeeInvite && (
                            <div className="pt-4 border-t border-slate-200">
                                <div className="text-xs font-semibold text-slate-800 uppercase tracking-wide mb-3">Dane osobowe</div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="text-xs font-medium text-slate-600 block mb-1">PESEL *</label>
                                        <input type="text" required value={form.pesel} onChange={(e) => setForm({ ...form, pesel: e.target.value })} className={inputCls} data-testid="register-pesel" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-medium text-slate-600 block mb-1">Data urodzenia *</label>
                                        <input type="date" required value={form.data_urodzenia} onChange={(e) => setForm({ ...form, data_urodzenia: e.target.value })} className={inputCls} data-testid="register-dob" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-medium text-slate-600 block mb-1">Numer telefonu (komórka) *</label>
                                        <input type="tel" required value={form.numer_telefonu} onChange={(e) => setForm({ ...form, numer_telefonu: e.target.value })} className={inputCls} data-testid="register-telefon" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-medium text-slate-600 block mb-1">Numer telefonu (Zebra)</label>
                                        <input type="tel" value={form.numer_telefonu_zebra} onChange={(e) => setForm({ ...form, numer_telefonu_zebra: e.target.value })} className={inputCls} placeholder="opcjonalne" data-testid="register-telefon-zebra" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-medium text-slate-600 block mb-1">Numer paszportu *</label>
                                        <input type="text" required value={form.numer_paszportu} onChange={(e) => setForm({ ...form, numer_paszportu: e.target.value })} className={inputCls} data-testid="register-paszport" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-medium text-slate-600 block mb-1">Numer prawa jazdy *</label>
                                        <input type="text" required value={form.numer_prawa_jazdy} onChange={(e) => setForm({ ...form, numer_prawa_jazdy: e.target.value })} className={inputCls} data-testid="register-prawo" />
                                    </div>
                                </div>
                                <div className="mt-2">
                                    <label className="text-xs font-medium text-slate-600 block mb-1">Numer blankietu prawa jazdy</label>
                                    <input type="text" value={form.numer_blankietu_prawa_jazdy} onChange={(e) => setForm({ ...form, numer_blankietu_prawa_jazdy: e.target.value })} className={inputCls} data-testid="register-blankiet" />
                                    <div className="text-[11px] text-slate-500 mt-1 italic">Zostaw pole puste w przypadku obcego prawa jazdy</div>
                                </div>
                                <div className="mt-2">
                                    <label className="text-xs font-medium text-slate-600 block mb-1">Adres *</label>
                                    <input type="text" required value={form.adres} onChange={(e) => setForm({ ...form, adres: e.target.value })} className={inputCls} placeholder="Ulica, nr, kod, miasto" data-testid="register-adres" />
                                </div>
                            </div>
                        )}

                        {isNewTenant && (
                            <div className="pt-4 border-t border-slate-200">
                                <div className="text-xs font-semibold text-slate-800 uppercase tracking-wide mb-3 flex items-center gap-1">
                                    <Buildings className="w-3.5 h-3.5" /> Dane firmy
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-slate-600 block mb-1">Nazwa firmy *</label>
                                    <input
                                        type="text"
                                        required
                                        value={form.company_name}
                                        onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                                        placeholder="np. Transport Sp. z o.o."
                                        className={inputCls}
                                        data-testid="register-company"
                                    />
                                </div>
                                <div className="mt-2">
                                    <label className="text-xs font-medium text-slate-600 block mb-1">NIP</label>
                                    <input
                                        type="text"
                                        value={form.nip}
                                        onChange={(e) => setForm({ ...form, nip: e.target.value })}
                                        placeholder="10 cyfr"
                                        className={inputCls}
                                        data-testid="register-nip"
                                    />
                                </div>
                            </div>
                        )}

                        {error && <div className="text-sm text-red-600 pt-1" data-testid="register-error">{error}</div>}
                        <div className="flex gap-2 pt-3">
                            <button
                                type="button"
                                onClick={() => { setStep(1); setPreview(null); }}
                                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-sm"
                            >
                                Wstecz
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-md text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                                data-testid="register-submit-btn"
                            >
                                {loading ? <CircleNotch className="w-4 h-4 animate-spin" /> : null}
                                Zarejestruj się
                            </button>
                        </div>
                    </form>
                )}
            </div>
            <div className="hidden md:flex flex-1 login-bg" />
        </div>
    );
}
